import React from "react";
import { NOTIFICATION_ACTION_TYPES } from "Constants";
import {
  useEnableActionMutation,
  useDisableActionMutation,
  useSetActionFrequencyMutation,
  Rate,
  ActionConfiguration
} from "generated/graphql";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { Select } from "collections/Form/Select";
import { LoadingIndicator, ToggleSwitchInput } from "elements";
import { FieldArray } from "formik";
import { getDividerIndex } from "./Helpers";

const NotificationType: React.FC<{
  actionConfig: ActionConfiguration;
  index: number;
  updateValue: (value: ActionConfiguration) => void;
  audienceId: string;
}> = ({ actionConfig, index, updateValue, audienceId }) => {
  const { t } = useTranslation();
  const [, setEnabledAction] = useEnableActionMutation();
  const [, setDisabledAction] = useDisableActionMutation();
  const [, setFrequency] = useSetActionFrequencyMutation();

  const { actionType, enabled, id: configId, allowedRates } = actionConfig;
  const { id: actionId, type } = actionType;
  const options = allowedRates.map(ar => {
    return {
      value: ar,
      text: t(ar as LocalKeys)
    };
  });

  async function handleToggleNotificationType() {
    if (!actionConfig.enabled && audienceId !== "") {
      const enabledActionResponse = await setEnabledAction({
        actionId,
        audienceId
      });
      const newConfiguration: ActionConfiguration = {
        ...actionConfig,
        enabled:
          enabledActionResponse.data?.ncEnableAction.enabled ??
          actionConfig.enabled
      };
      updateValue(newConfiguration);
    } else if (actionConfig.enabled && audienceId !== "") {
      const disabledActionReponse = await setDisabledAction({
        actionId,
        audienceId
      });
      const newConfiguration: ActionConfiguration = {
        ...actionConfig,
        enabled:
          disabledActionReponse.data?.ncDisableAction.enabled ??
          actionConfig.enabled
      };
      updateValue(newConfiguration);
    }
  }

  async function handleChangeFrequency(actionId: string, frequency: Rate) {
    const frequencyResponse = await setFrequency({ actionId, rate: frequency });
    const newConfiguration: ActionConfiguration = {
      ...actionConfig,
      rate: frequencyResponse.data?.ncSetFrequency.rate ?? actionConfig.rate
    };
    updateValue(newConfiguration);
  }

  const dividerIndex = getDividerIndex(allowedRates);

  return (
    <div className="form-group">
      <label
        className="col-3 text-right"
        htmlFor={"notification-types-input"}
        data-testid={type}
      >
        {t(NOTIFICATION_ACTION_TYPES[type])}:
      </label>
      <div className="col-1">
        <ToggleSwitchInput
          name={`notificationTypes[${index}].enabled`}
          checked={enabled}
          onChange={() => handleToggleNotificationType()}
          data-testid="notification-type-toggle"
        />
      </div>
      {enabled && (
        <div className="col-3 col-xl-2">
          <Select
            placeholder="Frequency"
            name={`notificationTypes[${index}].rate`}
            options={options}
            onSelectionChange={value =>
              handleChangeFrequency(configId, value as Rate)
            }
            dividerIndices={dividerIndex ? [dividerIndex] : []}
          />
        </div>
      )}
    </div>
  );
};

export const NotificationTypes: React.FC<{
  loading: boolean;
  actionConfigs: ActionConfiguration[];
  audienceId: string;
}> = ({ loading, actionConfigs, audienceId }) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="mt-15 ph-2" data-testid="notification-header">
        <label>{t("NOTIFICATION_TYPES")}</label>
      </div>
      {loading ? (
        <LoadingIndicator className="inline text-center" title="" />
      ) : (
        <FieldArray name="notificationTypes">
          {arrayHelpers => (
            <>
              {actionConfigs.map(
                (config: ActionConfiguration, index: number) => (
                  <NotificationType
                    key={config.id}
                    actionConfig={config}
                    index={index}
                    updateValue={value => arrayHelpers.replace(index, value)}
                    audienceId={audienceId}
                    data-testid="notification-type"
                  />
                )
              )}
            </>
          )}
        </FieldArray>
      )}
    </>
  );
};
